import React, { useState, useEffect } from 'react';
import TenantLayout from '../components/TenantLayout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { FileText, Send, X } from 'lucide-react';

export default function ExpenseDesk() {
    const { clientSlug } = useParams();
    const { api } = useAuth();
    const { t } = useLanguage();
    
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        party_id: '',
        category: 'purchase_master',
        line_items: [{ item: '', quantity: 1, rate: 0, gst_slab: 0, taxable_amount: 0, total_amount: 0 }]
    });
    
    const [parties, setParties] = useState([]);
    const [recentExpenses, setRecentExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [recipientEmail, setRecipientEmail] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [selectedTx, setSelectedTx] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');

    const categories = ['purchase_master', 'rent', 'payroll', 'utility', 'office_supplies', 'other_expense'];
    const gstSlabs = [0, 5, 12, 18, 28];

    const fetchData = async () => {
        try {
            const [txRes, partyRes] = await Promise.all([
                api.get(`/api/tenant/${clientSlug}/transactions`),
                api.get(`/api/tenant/${clientSlug}/parties`)
            ]);
            setRecentExpenses(txRes.data.filter(t => t.type === 'expense').slice(0, 15));
            setParties(partyRes.data.filter(p => p.role === 'supplier'));
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchData();
    }, [clientSlug]);

    const calculateTotals = (items) => {
        return items.reduce((acc, curr) => acc + (curr.total_amount || 0), 0);
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.line_items];
        const item = { ...newItems[index], [field]: value };
        
        if (field === 'quantity' || field === 'rate') {
            item.taxable_amount = (Number(item.quantity) || 0) * (Number(item.rate) || 0);
            item.total_amount = item.taxable_amount + (item.taxable_amount * (Number(item.gst_slab) || 0) / 100);
        } else if (field === 'gst_slab') {
            item.total_amount = item.taxable_amount + (item.taxable_amount * (Number(value) || 0) / 100);
        }
        
        newItems[index] = item;
        setFormData({ ...formData, line_items: newItems });
    };

    const addItem = () => {
        setFormData({
            ...formData,
            line_items: [...formData.line_items, { item: '', quantity: 1, rate: 0, gst_slab: 0, taxable_amount: 0, total_amount: 0 }]
        });
    };

    const removeItem = (index) => {
        if (formData.line_items.length <= 1) return;
        const newItems = formData.line_items.filter((_, i) => i !== index);
        setFormData({ ...formData, line_items: newItems });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.party_id) {
            toast.error(t("Please select a vendor/supplier"));
            return;
        }

        const validItems = formData.line_items.filter(i => i.item.trim() !== '' && i.total_amount > 0);
        if (validItems.length === 0) {
            toast.error(t("Please add at least one valid line item"));
            return;
        }

        setLoading(true);
        const selectedParty = parties.find(p => p._id === formData.party_id);
        const grandTotal = calculateTotals(validItems);
        const autoDescription = `${t("Bill from")} ${selectedParty?.name || t('Vendor')} - ₹${grandTotal.toFixed(2)}`;

        try {
            if (formData._id) {
                 await api.put(`/api/tenant/${clientSlug}/transactions/${formData._id}`, {
                    type: 'expense',
                    date: new Date(formData.date).toISOString(),
                    description: autoDescription,
                    party_id: formData.party_id,
                    party_name: selectedParty?.name,
                    amount: grandTotal,
                    payment_mode: formData.payment_mode || 'Cash',
                    category: formData.category,
                    line_items: validItems.map(item => ({
                        item: item.item,
                        quantity: Number(item.quantity),
                        rate: Number(item.rate),
                        gst_slab: Number(item.gst_slab),
                        taxable_amount: Number(item.taxable_amount),
                        total_amount: Number(item.total_amount)
                    }))
                });
                toast.success(t("Expense updated successfully"));
            } else {
                 await api.post(`/api/tenant/${clientSlug}/transactions`, {
                    type: 'expense',
                    date: new Date(formData.date).toISOString(),
                    description: autoDescription,
                    party_id: formData.party_id,
                    party_name: selectedParty?.name,
                    amount: grandTotal,
                    payment_mode: formData.payment_mode || 'Cash',
                    category: formData.category,
                    line_items: validItems.map(item => ({
                        item: item.item,
                        quantity: Number(item.quantity),
                        rate: Number(item.rate),
                        gst_slab: Number(item.gst_slab),
                        taxable_amount: Number(item.taxable_amount),
                        total_amount: Number(item.total_amount)
                    }))
                });
                toast.success(t("Expense recorded successfully"));
            }
            setFormData({ 
                date: new Date().toISOString().split('T')[0],
                party_id: '',
                party_name: '',
                category: 'purchase_master',
                payment_mode: 'Cash',
                line_items: [{ item: '', quantity: 1, rate: 0, gst_slab: 0, taxable_amount: 0, total_amount: 0 }]
            });
            await fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || t("Failed to record expense"));
        } finally {
            setLoading(false);
        }
    };

    const handlePreviewPDF = async (tx) => {
        try {
            const res = await api.get(`/api/tenant/${clientSlug}/transactions/${tx._id}/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            setPreviewUrl(url);
            setSelectedTx(tx);
            setPreviewModalOpen(true);
        } catch (err) {
            toast.error(t("Failed to generate PDF preview"));
        }
    };

    const formatCurrency = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val);

    const handleSendInvoice = async (e) => {
        e.preventDefault();
        if (!selectedTx || !recipientEmail) return;
        setSendingEmail(true);
        try {
            await api.post(`/api/tenant/${clientSlug}/transactions/${selectedTx._id}/email-invoice`, {
                recipient_email: recipientEmail
            });
            toast.success(t("Invoice sent successfully to") + " " + recipientEmail);
            setEmailModalOpen(false);
            setPreviewModalOpen(false);
            if (previewUrl) window.URL.revokeObjectURL(previewUrl);
            setPreviewUrl('');
            setSelectedTx(null);
            setRecipientEmail('');
        } catch (err) {
            toast.error(err.response?.data?.detail || t("Failed to send invoice"));
        } finally {
            setSendingEmail(false);
        }
    };

    const openEmailModal = (tx) => {
        setSelectedTx(tx);
        setEmailModalOpen(true);
    };

    const handleDeleteTx = async (id) => {
        if (window.confirm(t("Are you sure you want to delete this transaction?"))) {
            try {
                await api.delete(`/api/tenant/${clientSlug}/transactions/${id}`);
                toast.success(t("Deleted successfully"));
                fetchData();
            } catch (err) {
                toast.error(t("Failed to delete"));
            }
        }
    };
    
    const openEditModal = (tx) => {
        setFormData({
            _id: tx._id,
            date: tx.date.split('T')[0],
            party_id: tx.party_id,
            party_name: tx.party_name,
            category: tx.category,
            payment_mode: tx.payment_mode || 'Cash',
            line_items: tx.line_items || [{ item: '', quantity: 1, rate: 0, gst_slab: 0, taxable_amount: 0, total_amount: 0 }]
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        toast.info(t("Editing transaction"));
    };

    return (
        <TenantLayout title={t("Expense & Purchase Desk")} tagline={t("Track vendor bills, operational outlays & purchase receipts")}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Form Section */}
                <div className="lg:col-span-1 bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                    {formData._id ? (
                        <h2 className="text-lg font-bold text-[#0F284E] mb-6 flex items-center gap-2">
                            <span className="w-8 h-8 rounded-md bg-blue-100 text-blue-600 flex items-center justify-center">✎</span>
                            {t("Edit Expense Entry")}
                        </h2>
                    ) : (
                        <h2 className="text-lg font-bold text-[#0F284E] mb-6 flex items-center gap-2">
                            <span className="w-8 h-8 rounded-md bg-rose-100 text-rose-600 flex items-center justify-center">-</span>
                            {t("New Expense Entry")}
                        </h2>
                    )}
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">{t("Date")}</label>
                                <input 
                                    type="date"
                                    data-testid="expense-form-date-input"
                                    value={formData.date}
                                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                                    className="w-full h-12 sm:h-11 px-3.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0A192F] text-sm"
                                    required
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">{t("Category")}</label>
                                <select 
                                    data-testid="expense-form-category-select"
                                    value={formData.category}
                                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                                    className="w-full h-12 sm:h-11 px-3.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0A192F] text-sm"
                                >
                                    {categories.map(c => <option key={c} value={c}>{t(c.replace('_', ' ').toUpperCase())}</option>)}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">{t("Select Vendor (Supplier)")}</label>
                            <select 
                                data-testid="expense-form-party-select"
                                value={formData.party_id}
                                onChange={(e) => setFormData({...formData, party_id: e.target.value})}
                                className="w-full h-12 sm:h-11 px-3.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0A192F] text-sm"
                                required
                            >
                                <option value="" disabled>{t("-- Select Vendor --")}</option>
                                {parties.map(p => <option key={p._id} value={p._id}>{p.name} {p.gstin ? `(${p.gstin})` : ''}</option>)}
                            </select>
                        </div>
                        
                        <div className="pt-2">
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-bold text-[#0F284E]">{t("Bill Items Details")}</label>
                            </div>
                            
                            <div className="space-y-3">
                                {formData.line_items.map((item, idx) => (
                                    <div key={idx} className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex flex-col gap-2 relative">
                                        <div className="flex gap-2">
                                            <input 
                                                type="text"
                                                placeholder={t("Item Name / Service / Bill Ref")}
                                                value={item.item}
                                                onChange={(e) => handleItemChange(idx, 'item', e.target.value)}
                                                className="flex-1 h-12 sm:h-9 px-2.5 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]"
                                                required
                                            />
                                            {formData.line_items.length > 1 && (
                                                <button 
                                                    type="button" 
                                                    onClick={() => removeItem(idx)}
                                                    className="w-12 sm:w-9 h-12 sm:h-9 flex items-center justify-center text-rose-500 hover:bg-rose-100 rounded-md transition-colors"
                                                >
                                                    <X size={20} />
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                            <div>
                                                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">{t("Qty")}</label>
                                                <input type="number" min="0.1" step="0.1" value={item.quantity} onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)} className="w-full h-12 sm:h-9 px-2 rounded-md border border-slate-300 text-sm" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">{t("Rate (₹)")}</label>
                                                <input type="number" min="0" step="0.01" value={item.rate} onChange={(e) => handleItemChange(idx, 'rate', e.target.value)} className="w-full h-12 sm:h-9 px-2 rounded-md border border-slate-300 text-sm" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">{t("GST %")}</label>
                                                <select value={item.gst_slab} onChange={(e) => handleItemChange(idx, 'gst_slab', e.target.value)} className="w-full h-12 sm:h-9 px-2 rounded-md border border-slate-300 text-sm">
                                                    {gstSlabs.map(s => <option key={s} value={s}>{s}%</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">{t("Total")}</label>
                                                <div className="h-12 sm:h-9 px-2 flex items-center bg-white border border-slate-200 rounded-md text-sm font-mono font-bold text-rose-600 overflow-hidden">
                                                    {item.total_amount.toFixed(2)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button 
                                type="button" 
                                onClick={addItem}
                                className="mt-4 sm:mt-3 h-12 sm:h-auto px-4 sm:px-0 bg-rose-50 sm:bg-transparent rounded-lg sm:rounded-none text-sm sm:text-xs font-semibold text-rose-700 sm:text-rose-600 hover:text-rose-700 hover:underline flex items-center justify-center sm:justify-start gap-1 w-full sm:w-auto transition-colors"
                            >
                                + {t("Add Another Item")}
                            </button>
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center">
                            <span className="text-slate-600 font-medium">{t("Grand Total")}</span>
                            <span className="text-2xl font-mono font-bold text-rose-600">
                                {formatCurrency(calculateTotals(formData.line_items))}
                            </span>
                        </div>

                        <div className="mt-4">
                            <label className="block text-sm font-medium text-slate-700 mb-1">{t("Payment Mode")}</label>
                            <select 
                                value={formData.payment_mode || 'Cash'}
                                onChange={(e) => setFormData({...formData, payment_mode: e.target.value})}
                                className="w-full h-12 sm:h-11 px-3.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0A192F] text-sm"
                            >
                                <option value="Cash">{t("Cash")}</option>
                                <option value="Bank">{t("Bank Transfer")}</option>
                                <option value="Cheque">{t("Cheque")}</option>
                                <option value="UPI">{t("UPI")}</option>
                                <option value="Other">{t("Other")}</option>
                            </select>
                        </div>
                        
                        <button 
                            type="submit" 
                            data-testid="expense-form-submit-button"
                            disabled={loading}
                            className="w-full h-12 mt-4 bg-[#0A192F] hover:bg-[#0F284E] text-white font-medium rounded-lg shadow-sm transition-colors flex justify-center items-center"
                        >
                            {loading ? t('Saving...') : (formData._id ? t('Update Expense') : t('Record Expense'))}
                        </button>
                        {formData._id && (
                            <button 
                                type="button" 
                                onClick={() => setFormData({
                                    date: new Date().toISOString().split('T')[0],
                                    party_id: '',
                                    party_name: '',
                                    category: 'purchase_master',
                                    payment_mode: 'Cash',
                                    line_items: [{ item: '', quantity: 1, rate: 0, gst_slab: 0, taxable_amount: 0, total_amount: 0 }]
                                })}
                                className="w-full h-12 mt-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-lg shadow-sm transition-colors flex justify-center items-center"
                            >
                                {t('Cancel Edit')}
                            </button>
                        )}
                    </form>
                </div>

                {/* Recent List Section */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                    <div className="p-6 border-b border-slate-100 bg-slate-50/50">
                        <h2 className="text-lg font-bold text-[#0F284E]">{t("Recent Purchases")}</h2>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left whitespace-nowrap min-w-[600px]">
                            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase font-mono tracking-wider text-slate-600">
                                <tr>
                                    <th className="px-6 py-3">{t("Date")}</th>
                                    <th className="px-6 py-3">{t("Description")}</th>
                                    <th className="px-6 py-3">{t("Category")}</th>
                                    <th className="px-6 py-3">{t("Mode")}</th>
                                    <th className="px-6 py-3 text-right">{t("Amount")}</th>
                                    <th className="px-6 py-3 text-center">{t("Actions")}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {recentExpenses.map((tx) => (
                                    <tr key={tx._id} className="hover:bg-slate-50/50">
                                        <td className="px-6 py-4 whitespace-nowrap text-slate-500">
                                            {new Date(tx.date).toLocaleDateString('en-IN')}
                                        </td>
                                        <td className="px-6 py-4 font-medium text-slate-900">
                                            {tx.description}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA]">
                                                {t(tx.category.replace('_', ' ').toUpperCase())}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-slate-600 text-xs font-semibold">
                                            {t(tx.payment_mode || 'Cash')}
                                        </td>
                                        <td className="px-6 py-4 text-right font-mono font-bold text-rose-600">
                                            -{formatCurrency(tx.amount)}
                                        </td>
                                        <td className="px-6 py-4 text-center space-x-2">
                                             <button 
                                                onClick={() => handlePreviewPDF(tx)}
                                                data-testid="preview-transaction-btn"
                                                className="inline-flex items-center gap-1.5 px-3 py-2 sm:py-1.5 bg-slate-100 hover:bg-[#0A192F]/10 hover:text-[#0A192F] text-slate-600 rounded-md text-xs sm:text-xs font-semibold transition-colors min-w-[44px] justify-center"
                                                title="Preview PDF Receipt"
                                             >
                                                {t("Preview")}
                                             </button>
                                             <button 
                                                onClick={() => openEditModal(tx)}
                                                data-testid="edit-transaction-btn"
                                                className="inline-flex items-center gap-1.5 px-3 py-2 sm:py-1.5 bg-slate-100 hover:bg-blue-500/20 hover:text-blue-700 text-slate-600 rounded-md text-xs sm:text-xs font-semibold transition-colors min-w-[44px] justify-center"
                                                title="Edit Entry"
                                             >
                                                {t("Edit")}
                                             </button>
                                             <button 
                                                onClick={() => handleDeleteTx(tx._id)}
                                                data-testid="delete-transaction-btn"
                                                className="inline-flex items-center gap-1.5 px-3 py-2 sm:py-1.5 bg-slate-100 hover:bg-rose-500/20 hover:text-rose-700 text-slate-600 rounded-md text-xs sm:text-xs font-semibold transition-colors min-w-[44px] justify-center"
                                                title="Delete Entry"
                                             >
                                                {t("Delete")}
                                             </button>
                                        </td>
                                    </tr>
                                ))}
                                {recentExpenses.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center text-slate-500">{t("No recent expense entries found.")}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {/* PDF Preview Modal */}
            {previewModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full h-[90vh] flex flex-col border border-slate-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <h3 className="font-bold text-[#0F284E] flex items-center gap-2">
                                <FileText size={18} className="text-[#EAB308]" />
                                {t("Receipt Preview")}
                            </h3>
                            <button 
                                onClick={() => {
                                    setPreviewModalOpen(false);
                                    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
                                    setPreviewUrl('');
                                }} 
                                className="text-slate-400 hover:text-slate-700 p-1"
                            >
                                <span className="text-xl font-bold">×</span>
                            </button>
                        </div>
                        <div className="flex-1 bg-slate-200/50 p-4">
                            <iframe 
                                src={previewUrl} 
                                className="w-full h-full rounded-lg shadow-sm bg-white"
                                title="PDF Preview"
                            />
                        </div>
                        <div className="p-4 border-t border-slate-100 flex justify-end gap-3 bg-white">
                            <button 
                                onClick={() => {
                                    setPreviewModalOpen(false);
                                    openEmailModal(selectedTx);
                                }}
                                className="px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg font-medium shadow-sm transition-colors flex items-center gap-2"
                            >
                                <Send size={16} /> {t("Email PDF")}
                            </button>
                            <a 
                                href={previewUrl} 
                                download={`receipt_${selectedTx?._id?.slice(-6) || 'outlay'}.pdf`}
                                className="px-5 py-2 bg-[#0A192F] hover:bg-[#0F284E] text-white rounded-lg font-medium shadow-sm transition-colors flex items-center gap-2"
                            >
                                {t("Download PDF")}
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Email Invoice Modal */}
            {emailModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/80">
                            <h3 className="font-bold text-[#0F284E] flex items-center gap-2">
                                <FileText size={18} className="text-[#EAB308]" />
                                {t("Email Invoice PDF")}
                            </h3>
                            <button onClick={() => setEmailModalOpen(false)} className="text-slate-400 hover:text-slate-700">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSendInvoice} className="p-6">
                            <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 mb-6 text-sm">
                                <div className="text-slate-500 mb-1">{t("Invoice Details:")}</div>
                                <div className="font-medium text-slate-900">{selectedTx?.description}</div>
                                <div className="font-mono font-bold text-rose-600 mt-1">{formatCurrency(selectedTx?.amount)}</div>
                            </div>

            {/* Edit Modal Placeholders (To Be Implemented Fully if needed) */}

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-slate-700 mb-2">{t("Recipient Email Address")}</label>
                                <input 
                                    type="email"
                                    value={recipientEmail}
                                    onChange={(e) => setRecipientEmail(e.target.value)}
                                    placeholder="vendor@example.com"
                                    className="w-full h-11 px-3.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0A192F]"
                                    required
                                />
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button 
                                    type="button" 
                                    onClick={() => setEmailModalOpen(false)}
                                    className="px-4 py-2 rounded-lg text-slate-600 font-medium hover:bg-slate-100 transition-colors"
                                >
                                    {t("Cancel")}
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={sendingEmail}
                                    className="px-5 py-2 bg-[#0A192F] hover:bg-[#0F284E] text-white rounded-lg font-medium shadow-sm flex items-center gap-2 transition-colors disabled:opacity-70"
                                >
                                    {sendingEmail ? (
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <><Send size={16} /> {t("Send PDF")}</>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </TenantLayout>
    );
}
